import type { ProfileModel } from "../types/models";

// "news-bell" experiment: a bell in the app header with an unread badge that
// opens a "What's new" panel (components/NewsMenu.tsx, items in
// const/newsItems.ts), vs no bell (control). It measures whether announcing
// features in the app makes people visit and use them: visits to /mcp and
// /integrations/zapier, MCP clients and Zapier connected ("OAuth App
// Connected"), and MCP tool calls (klap-api "… Tool Called").
// The flag is evaluated, and the exposure recorded, in the header of every
// signed-in page (UserNav), in both arms. Delete with the experiment.

export type NewsBellVariant = "control" | "test";

export const NEWS_BELL_FLAG_KEY = "news-bell";

// `?news_bell=test` (or `=control`) on any signed-in page shows that arm
// without evaluating the flag, so nobody is enrolled: for QA and previews.
export const NEWS_BELL_PREVIEW_PARAM = "news_bell";

// Anything but the test arm (control, not enrolled, flag missing) has no bell.
export const newsBellVariantFor = (
  variant: string | boolean | undefined | null,
): NewsBellVariant => (variant === "test" ? "test" : "control");

// Signed-in, non-guest accounts only: guests never see the header.
export const isNewsBellEligible = (
  profile?: Pick<ProfileModel, "isGuest"> | null,
): boolean => !!profile && !profile.isGuest;

// Whether the flags PostHog holds were evaluated for the signed-in account.
// On a browser's first sign-in, identify() switches the distinct id at once,
// but the flags loaded (or still loading) for the anonymous visitor stay until
// the reload it queues answers, and they can hash into the other arm.
// identify() sets featureFlags.$anon_distinct_id, and posthog-js clears it
// when that reload succeeds, before calling the onFeatureFlags callbacks.
export const hasAccountFlags = (
  posthog: {
    get_distinct_id: () => string;
    featureFlags?: { $anon_distinct_id?: string };
  },
  profileId: string,
): boolean =>
  posthog.get_distinct_id() === profileId &&
  !posthog.featureFlags?.$anon_distinct_id;

export const SEEN_NEWS_KEY = "klap_news_seen";

// Ids of the news items this browser has seen in an opened panel.
export const readSeenNewsIds = (): string[] => {
  try {
    const stored = JSON.parse(localStorage.getItem(SEEN_NEWS_KEY) ?? "[]");
    return Array.isArray(stored)
      ? stored.filter((id): id is string => typeof id === "string")
      : [];
  } catch {
    // No storage (private mode) or a corrupt value: everything is unread.
    return [];
  }
};

// Adds `ids` to the ids already seen and returns the whole list.
export const markNewsSeen = (ids: string[]): string[] => {
  const seen = Array.from(new Set([...readSeenNewsIds(), ...ids]));
  try {
    localStorage.setItem(SEEN_NEWS_KEY, JSON.stringify(seen));
  } catch {
    // No storage: the badge comes back on the next page load, nothing worse.
  }
  return seen;
};

export const unreadNewsIds = (
  items: { id: string }[],
  seenIds: string[],
): string[] =>
  items.map((item) => item.id).filter((id) => !seenIds.includes(id));
